import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Transition,
} from "@headlessui/react";
import clsx from "clsx";
import {
    Archive,
    ArrowUpRightIcon,
    Bookmark,
    MoreHorizontal,
    Trash2Icon,
} from "lucide-react";
import { JobsEmptyState } from "../../components/Jobs/JobsEmptyState";
import { Button } from "@/components/Button/Button";
import { SmartMatchIcon } from "@/components/Icons/Icons";
import { AppText } from "@/components/Text/AppText";
import { formatJobDate, formatJobLocation } from "@/pages/Jobs/utils";
import { IJobListing } from "@/types/job";

type JobBoardList = {
    searchResults: {
        jobs: IJobListing[];
        total: number;
    } | null;
    searchFilters: {
        term: string;
        location: string;
    };
    showActions?: boolean;
    saveJob: (job: IJobListing) => void;
    openSmartMatchModal: (job: IJobListing) => void;
    loadingJobIds?: Set<string>;
};

export const JobBoardList = ({
    searchResults,
    searchFilters,
    showActions = false,
    saveJob,
    openSmartMatchModal,
    loadingJobIds = new Set(),
}: JobBoardList) => {
    return (
        <div className="lg:hidden">
            {searchResults?.jobs.length === 0 ? (
                <JobsEmptyState
                    term={searchFilters.term}
                    location={searchFilters.location}
                />
            ) : (
                <div className="space-y-4">
                    {searchResults?.jobs.map(job => {
                        const isLoading = loadingJobIds.has(job.id);
                        return (
                            <div
                                key={job.id}
                                className={clsx(
                                    "relative rounded-lg bg-white p-4 shadow-sm ring-2 ring-inset",
                                )}
                            >
                                <div className="mb-2 flex items-start justify-between gap-3 border-b border-neutral-100 pb-3">
                                    <div>
                                        <AppText
                                            variant="contextheading"
                                            className="line-clamp-1 text-neutral-900"
                                        >
                                            {job.title}
                                        </AppText>
                                        {!!job.organization && (
                                            <AppText
                                                variant="regular"
                                                className="mt-1 line-clamp-1 text-neutral-700"
                                            >
                                                {job.organization}
                                            </AppText>
                                        )}
                                    </div>
                                    {showActions && (
                                        <button
                                            onClick={() => saveJob(job)}
                                            disabled={isLoading}
                                            className={clsx(
                                                "rounded-lg p-1 hover:bg-neutral-100",
                                                isLoading &&
                                                    "cursor-not-allowed opacity-50",
                                            )}
                                        >
                                            {isLoading ? (
                                                <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                            ) : (
                                                <Bookmark size={20} />
                                            )}
                                        </button>
                                    )}
                                </div>

                                <div className="mb-2 flex items-center gap-4">
                                    <div className="hidden items-center gap-2 xs:flex">
                                        <AppText
                                            variant="labelsbuttons"
                                            className="text-nowrap text-neutral-900"
                                        >
                                            {formatJobDate(job.date_posted)}
                                        </AppText>
                                    </div>
                                    {job.locations_derived?.length > 0 && (
                                        <div className="hidden items-center gap-2 xs:flex ">
                                            <AppText
                                                variant="labelsbuttons"
                                                className="line-clamp-1 text-neutral-900"
                                            >
                                                {formatJobLocation(
                                                    job.locations_derived,
                                                )}
                                            </AppText>
                                        </div>
                                    )}
                                </div>

                                {showActions && (
                                    <div
                                        className={clsx(
                                            "flex items-center border-t border-neutral-100 pt-3",
                                            "justify-between",
                                        )}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Button
                                                color="gradient"
                                                variant="outline"
                                                onClick={() =>
                                                    openSmartMatchModal(job)
                                                }
                                                disabled={isLoading}
                                                leftIcon={
                                                    <SmartMatchIcon
                                                        gradient
                                                        className="h-7 w-7"
                                                    />
                                                }
                                            >
                                                Smart Match
                                            </Button>
                                        </div>
                                        <Menu
                                            as="div"
                                            className="relative inline-block text-left"
                                        >
                                            {({ open }) => (
                                                <>
                                                    <MenuButton>
                                                        <button className="rounded-lg p-1 hover:bg-neutral-100">
                                                            <MoreHorizontal
                                                                size={20}
                                                                className="text-neutral-900"
                                                            />
                                                        </button>
                                                    </MenuButton>
                                                    <Transition
                                                        show={open}
                                                        enter="transition duration-100 ease-out"
                                                        enterFrom="transform scale-95 opacity-0"
                                                        enterTo="transform scale-100 opacity-100"
                                                        leave="transition duration-75 ease-out"
                                                        leaveFrom="transform scale-100 opacity-100"
                                                        leaveTo="transform scale-95 opacity-0"
                                                    >
                                                        <MenuItems
                                                            static
                                                            className="absolute right-0 z-10 flex w-40 origin-top-right flex-col overflow-hidden rounded-lg bg-neutral-50 shadow-lg ring-2 ring-neutral-300 focus:outline-none"
                                                        >
                                                            {job.url && (
                                                                <MenuItem
                                                                    as="a"
                                                                    href={
                                                                        job.url
                                                                    }
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="flex h-10 w-full cursor-pointer items-center gap-2 border-b-2 border-neutral-300 px-3 text-app-labelsbuttons-4-mobile hover:bg-neutral-100 md:text-app-labelsbuttons-4-desktop"
                                                                >
                                                                    <ArrowUpRightIcon className="h-5 w-5" />
                                                                    View Job
                                                                </MenuItem>
                                                            )}
                                                            <MenuItem
                                                                as="button"
                                                                className="flex h-10 w-full cursor-pointer items-center gap-2 border-b-2 border-neutral-300 px-3 text-app-labelsbuttons-4-mobile hover:bg-neutral-100 md:text-app-labelsbuttons-4-desktop"
                                                            >
                                                                <Archive className="h-5 w-5" />
                                                                Archive
                                                            </MenuItem>
                                                            <MenuItem
                                                                as="button"
                                                                className="flex h-10 w-full cursor-pointer items-center gap-2 px-3 text-app-labelsbuttons-4-mobile hover:bg-neutral-100 md:text-app-labelsbuttons-4-desktop"
                                                            >
                                                                <Trash2Icon className="h-5 w-5" />
                                                                Delete
                                                            </MenuItem>
                                                        </MenuItems>
                                                    </Transition>
                                                </>
                                            )}
                                        </Menu>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
